import type { Edge, Node } from '@xyflow/react';
import { edgeId, type FlowDefinition, type FlowEdge, type FlowNode, type NodeKind } from './types';
import { layoutFlow, type FlowLayout } from './layout';
import type { DiagnosticIndex, Severity } from './diagnostics';
import type { DiffIndex, DiffState } from './diff';
import type { XY } from '@/store/designerSlice';

/** Data carried on each xyflow node — the raw IR node plus render/overlay flags. */
export interface OlNodeData extends Record<string, unknown> {
  node: FlowNode;
  /** Worst diagnostic severity on this node (badge). */
  diag?: Severity;
  /** Number of diagnostics on this node. */
  diagCount?: number;
  /** Diff state vs the compared version (canvas highlight). */
  diffState?: DiffState;
}
export type OlNode = Node<OlNodeData>;

export interface OlEdgeData extends Record<string, unknown> {
  kind: FlowEdge['kind'];
  onStatus?: string;
  guard?: string;
  binding?: { source: string; target: string };
  obligations?: string[];
  diag?: Severity;
  diffState?: DiffState;
}
export type OlEdge = Edge<OlEdgeData>;

/** Registered xyflow node-type key for an IR node kind. */
export function nodeTypeFor(kind: NodeKind): string {
  switch (kind) {
    case 'decision':
      return 'decision';
    case 'humanTask':
    case 'humanReviewTask':
      return 'humanTask';
    case 'subFlow':
    case 'dynamicBranch':
      return 'subFlow';
    case 'multiSourceScoring':
      return 'multiSourceScoring';
    case 'terminal':
      return 'terminal';
    case 'join':
    case 'parallel':
      return 'flowControl';
    case 'llmTask':
    case 'confidenceGate':
    case 'agentLoop':
    case 'shadowEval':
    case 'retrievalTask':
    case 'memoryTask':
    case 'policyGuard':
    case 'agentNegotiation':
      return 'ai';
    case 'capability':
    default:
      return 'capability';
  }
}

export interface RenderGraph {
  nodes: OlNode[];
  edges: OlEdge[];
  layout: FlowLayout;
}

export interface RenderOptions {
  dataLayerVisible: boolean;
  editable?: boolean;
  /** Ephemeral drag-position overrides (id → x/y); absent = deterministic lane layout. */
  positions?: Record<string, XY>;
  diagIndex?: DiagnosticIndex;
  diffIndex?: DiffIndex;
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
}

/**
 * Build the xyflow graph from a base FlowDefinition. Control edges (sequence/status) attach to the
 * top/bottom handles (the vertical spine); data edges attach to side handles and are hideable. In
 * edit mode nodes are draggable + connectable; diagnostics/diff overlays ride on node/edge data.
 */
export function toRenderGraph(def: FlowDefinition, opts: RenderOptions): RenderGraph {
  const layout = layoutFlow(def);
  const { editable, positions, diagIndex, diffIndex, selectedNodeId, selectedEdgeId } = opts;

  const nodes: OlNode[] = layout.nodes.map((ln) => {
    const id = ln.node.id;
    const override = positions?.[id];
    return {
      id,
      type: nodeTypeFor(ln.node.kind),
      position: override ?? { x: ln.x, y: ln.y },
      data: {
        node: ln.node,
        diag: diagIndex?.worstNode.get(id),
        diagCount: diagIndex?.byNode.get(id)?.length,
        diffState: diffIndex?.byLocus.get(id),
      },
      draggable: Boolean(editable),
      connectable: Boolean(editable),
      focusable: true,
      selected: id === selectedNodeId,
    };
  });

  const edges: OlEdge[] = (def.edges ?? []).map((e) => {
    const isData = e.kind === 'data';
    const id = e.id ?? edgeId(e);
    return {
      id,
      source: e.from,
      target: e.to,
      type: 'ol',
      sourceHandle: isData ? 'data-r' : 'out',
      targetHandle: isData ? 'data-l' : 'in',
      hidden: isData && !opts.dataLayerVisible,
      selectable: true,
      selected: id === selectedEdgeId,
      data: {
        kind: e.kind,
        onStatus: e.onStatus,
        guard: e.guard,
        binding: e.binding,
        obligations: e.obligations,
        diag: diagIndex?.worstEdge.get(id),
        diffState: diffIndex?.byLocus.get(id),
      },
      zIndex: isData ? 0 : 1,
    };
  });

  return { nodes, edges, layout };
}
